import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import client from "../contentfulClient";
import { useTranslation } from "react-i18next";
import "./Menu.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faJar, faStar, faPercent } from "@fortawesome/free-solid-svg-icons";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { MyContext } from "../Context/Context";
import { isClosedDubai0400To1200 } from "../utils/time";
import {
  applyOfferOverrides,
  getOfferOverrides,
  OFFERS_UPDATED_EVENT,
} from "../utils/offers";
import { fetchOffers } from "../utils/api";
import LazyImage from "./LazyImage";

const options = {
  renderText: (text) => {
    return text.split(/(\d+\s?[a-zA-Z]*)/).map((part, index) => {
      if (/^\d+\s?[a-zA-Z]*$/.test(part)) {
        return (
          <span key={index} style={{ color: "#e63946", fontWeight: "bold" }}>
            {part}
          </span>
        );
      }
      return part;
    });
  },
};

const categoryIcons = {
  "Best Seller": <FontAwesomeIcon icon={faStar} className="icon best-star" />, 
  Offer: <FontAwesomeIcon icon={faPercent} className="icon offer" />, 
  All: "🍽️",
  "Chicken Shawarma": "🌯",
  "Chicken Comboo Meals": "🍗",
  "Meat Shawarma": "🥩",
  "Shawarma mix": "🍖",
  "Chicken Burgers": "🍔",
  "Beef Burgers": "🥓",
  Fries: "🍟",
  Beverages: "🥤",
  Sauces: <FontAwesomeIcon icon={faJar} className="icon sauces" />,
};

const Menu = () => {
  const { i18n, t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [popupImage, setPopupImage] = useState(null);
  const [baseMenuData, setBaseMenuData] = useState([]);
  const [offerOverrides, setOfferOverrides] = useState(null); // null = not loaded yet
  const [menuData, setMenuData] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const { basketItem, setBasketItem } = useContext(MyContext);

  // Ref to items grid and helper to scroll it into view on small screens
  const sectionRef = useRef(null);
  const gridRef = useRef(null);
  const scrollToGrid = () => {
    try {
      if (typeof window === "undefined") return;
      if (!window.matchMedia("(max-width: 768px)").matches) return;
      const el =
        sectionRef.current ||
        gridRef.current ||
        document.querySelector(".menu-grid");
      if (!el) return;
      const NAV_OFFSET = 150; // fixed navbar height on mobile
      const y =
        el.getBoundingClientRect().top + window.pageYOffset - NAV_OFFSET;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    } catch (_) {}
  };

  // Closed-hours unified (Dubai time: 04:00–12:00)
  const [isClosed, setIsClosed] = useState(isClosedDubai0400To1200());
  useEffect(() => {
    const id = setInterval(() => setIsClosed(isClosedDubai0400To1200()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    client
      .getEntries({ content_type: "menuItem" })
      .then((response) => {
        const items = response.items.map((entry) => ({
          id: entry.sys.id,
          name: {
            en: entry.fields.nameEn,
            ar: entry.fields.nameAr,
          },
          description: {
            en: entry.fields.descriptionEn,
            ar: entry.fields.descriptionAr,
          },
          price: entry.fields.price,
          image: entry.fields.image ? entry.fields.image.fields.file.url : "",
          category: entry.fields.category,
        }));
        // Tag best sellers by Arabic and English name keywords
        const bestAr = [
          "وجبة شاورما دجاج عربي",
          "سندويش شاورما دجاج كبير",
          "دوز كلاسيك برغر",
          "سندويش شاورما دجاج خبز صمون مع جبنة",
          "ساندويتش دجاج سوبريم",
          "ساندويتش شيش طاووق دجاج",
        ].map((s) => (s || "").toString().trim());
        const bestEn = [
          "Dose Classic Burger",
          "Medium Chicken Sandwich - 80g",
          "Medium Chicken Sandwich 80g",
          "Large Chicken Sandwich 100g",
          "Large Chicken Sandwich - 100g",
          "Fries Sandwich 100g with Mozzarella Cheese",
          "Fries",
          "Medium Meat Sandwich 80g",
          "Medium Meat Sandwich - 80g",
        ].map((s) => (s || "").toString().trim().toLowerCase());
        const bestRegex = [
          /\bmedium\s+meat\s+sandwich\s*(?:-\s*)?80\s*g\b/i,
        ];
        const excludeEn = [
          "Fries Sandwich 120g",
          "Meat Arabic Shawarma meal with fries 280g",
          "Meat Arabic Shawarma Combo Meal 280g with fries",
          "Meat Arabic Combo",
        ].map((s) => (s || "").toString().trim().toLowerCase());
        const excludeRegex = [
          /\bfries\s+sandwich\s*120\s*g\b/i,
          /\bmeat\s+arabic\s+shawarma\s+combo\s+meal\b/i,
          /\bmeat\s+arabic\s+shawarma\s+meal\b/i,
        ];

        const tagged = items.map((it) => {
          const nameAr = (it?.name?.ar || "").toString();
          const nameEn = (it?.name?.en || "").toString();
          const isBestAr = bestAr.some((kw) => nameAr.includes(kw));
          const lowerEn = nameEn.trim().toLowerCase();
          const isBestEn =
            bestEn.some((kw) => lowerEn.includes(kw)) ||
            bestRegex.some((re) => re.test(nameEn));
          const isExcluded =
            excludeEn.some((kw) => lowerEn.includes(kw)) ||
            excludeRegex.some((re) => re.test(nameEn));
          const isBest = (isBestAr || isBestEn) && !isExcluded;
          if (!isBest) return it;
          const discount = Math.round(Number(it.price || 0) * 0.75 * 100) / 100;
          return { ...it, isBestSeller: true, discountPrice: discount };
        });
        setBaseMenuData(tagged);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    let retryTimer;
    // Server offers are the source of truth. Prices aren't shown until they
    // arrive: rendering first with no overrides would flash the built-in
    // default discount on items an admin has switched off, and a customer
    // tapping "+" in that window would carry the wrong price to checkout.
    const loadOffers = (attempt = 0) => {
      fetchOffers()
        .then((offers) => setOfferOverrides(offers || {}))
        .catch(() => {
          if (attempt < 2) {
            retryTimer = setTimeout(() => loadOffers(attempt + 1), 1500);
            return;
          }
          // Checkout re-prices against the server before ordering anyway.
          setOfferOverrides((current) => current || getOfferOverrides());
        });
    };
    const reload = () => loadOffers();

    loadOffers();
    window.addEventListener(OFFERS_UPDATED_EVENT, reload);
    window.addEventListener("storage", reload);
    return () => {
      clearTimeout(retryTimer);
      window.removeEventListener(OFFERS_UPDATED_EVENT, reload);
      window.removeEventListener("storage", reload);
    };
  }, []);

  useEffect(() => {
    if (!baseMenuData.length || !offerOverrides) return undefined;

    setMenuData(applyOfferOverrides(baseMenuData, offerOverrides));
  }, [baseMenuData, offerOverrides]);

  useEffect(() => {
    setShowAll(false);
  }, [selectedCategory, menuData.length]);

  const norm = (s) => (s || "").toString().trim().toLowerCase();
  const nameKey = (s) =>
    (s || "").toString().toLowerCase().replace(/\s+/g, "").replace(/-/g, "");
  const PRIORITY_KEYS_ALL = [
    // Chicken: make Medium 80g first, then Large 100g
    "mediumchickensandwich80g",
    "largechickensandwich100g",
  ];
  const PRIORITY_KEYS_BEST = [
    // Bestsellers: Medium Chicken first, then Large
    "mediumchickensandwich80g",      // #1
    "largechickensandwich100g",      // #2
    // Meat: Medium first, then Large
    "mediummeatsandwich80g",         // #3
    "largemeatsandwich100g",         // #4
    "doseclassicburger",             // #5
  ];
  const PRIORITY_KEYS_CHICKEN = [
    ...PRIORITY_KEYS_ALL,
    "classicrussianshawarma80g",
  ];
  const PRIORITY_KEYS_MEAT = [
    // Meat: make Medium 80g first, then Large 100g
    "mediummeatsandwich80g",
    "largemeatsandwich100g",
  ];
  const DEMOTE_KEYS_CHICKEN = [
    "chickenshawarmaplatewithfries250g",
    "familymealwithfries480g",
    "chickenarabicshawarmamealwithfries280g",
  ];

  const withPriority = (arr = [], keys = PRIORITY_KEYS_ALL, demoteKeys = []) => {
    return [...arr]
      .map((it, idx) => {
        const kEn = nameKey(it?.name?.en);
        const prio = keys.indexOf(kEn);
        const demote = demoteKeys.includes(kEn);
        return {
          it,
          idx,
          prio: prio === -1 ? Number.POSITIVE_INFINITY : prio,
          demote,
        };
      })
      .sort((a, b) => {
        if (a.demote !== b.demote) return a.demote - b.demote; // non-demoted first
        if (a.prio !== b.prio) return a.prio - b.prio; // then priority order
        return a.idx - b.idx; // stable
      })
      .map((x) => x.it);
  };

  let filteredItems;
  if (selectedCategory === "Best Seller") {
    filteredItems = menuData.filter((item) => item.isBestSeller);
  } else if (selectedCategory === "Offer") {
    filteredItems = menuData.filter((item) => item.isBestSeller);
  } else if (selectedCategory === "All") {
    // Explicit category order for the All view
    const pick = (label) =>
      menuData.filter((item) => norm(item.category) === norm(label));

    const chickenShawarma = withPriority(
      pick("Chicken Shawarma"),
      PRIORITY_KEYS_ALL
    );
    const meatShawarma = withPriority(
      pick("Meat Shawarma"),
      PRIORITY_KEYS_MEAT
    );
    const ordered = [
      ...chickenShawarma, // Chicken Shawarma first
      ...meatShawarma,
      ...pick("Shawarma mix"),
      ...pick("Chicken Comboo Meals"),
      ...pick("Chicken Burgers"),
      ...pick("Beef Burgers"),
      ...pick("Fries"),
      ...pick("Beverages"),
      ...pick("Sauces"),
    ];

    const used = new Set(ordered.map((it) => it.id));
    const remaining = menuData.filter((it) => !used.has(it.id));
    filteredItems = [...ordered, ...remaining];
  } else {
    filteredItems = menuData.filter(
      (item) => norm(item.category) === norm(selectedCategory)
    );
    if (norm(selectedCategory) === "chicken shawarma") {
      filteredItems = withPriority(
        filteredItems,
        PRIORITY_KEYS_CHICKEN,
        DEMOTE_KEYS_CHICKEN
      );
    } else if (norm(selectedCategory) === "meat shawarma") {
      filteredItems = withPriority(filteredItems, PRIORITY_KEYS_MEAT);
    }
  }

  // For Best Seller/Offer, sort so Chicken items appear first, then Meat, then others
  if (
    selectedCategory === "Best Seller" ||
    selectedCategory === "Offer"
  ) {
    const prio = (c = "") => {
      const v = norm(c);
      if (v.includes("chicken")) return 0;
      if (v.includes("meat")) return 1;
      return 2;
    };
    filteredItems = [...(filteredItems || [])].sort((a, b) => {
      const pa = prio(a?.category);
      const pb = prio(b?.category);
      if (pa !== pb) return pa - pb;
      // tie-breaker: name
      const na = norm(a?.name?.en || a?.name?.ar);
      const nb = norm(b?.name?.en || b?.name?.ar);
      return na.localeCompare(nb);
    });
    // After category sort, enforce explicit order at the front
    const keys = selectedCategory === "Best Seller"
      ? PRIORITY_KEYS_BEST
      : PRIORITY_KEYS_ALL; // Offer keeps top-2 prioritization
    filteredItems = withPriority(filteredItems, keys);
  }

  filteredItems = filteredItems || [];
  const INITIAL_BATCH = 12;
  const visibleItems = showAll
    ? filteredItems
    : filteredItems.slice(0, INITIAL_BATCH);
  const hasHiddenItems =
    !showAll && filteredItems.length > INITIAL_BATCH;

  // Quantities (per item) and broadcast to Navbar/Checkout
  const [quantities, setQuantities] = useState({});

  const publishTotal = useCallback((q) => {
    const total = Object.values(q).reduce((sum, v) => sum + (v || 0), 0);

    const items = Object.entries(q)
      .filter(([_, qty]) => (qty || 0) > 0)
      .map(([id, qty]) => {
        const it = menuData.find((m) => m.id === id);
        if (!it) return null;
        // Use discounted price if available (e.g., Best Seller deals)
        const hasDiscount =
          typeof it.discountPrice === "number" &&
          Number(it.discountPrice) < Number(it.price || 0);
        const unitPrice = Number(it.discountPrice ?? it.price ?? 0);
        const originalPrice = Number(it.price ?? unitPrice);
        return {
          id,
          qty,
          price: unitPrice,
          originalPrice,
          isDiscounted: hasDiscount,
          image: it.image,
          name: it.name,
          category: it.category,
        };
      })
      .filter(Boolean);

    const totalPrice = items.reduce((sum, it) => sum + it.price * it.qty, 0);

    // 1) Store in Context: basketItem
    if (typeof setBasketItem === "function") {
      setBasketItem({ items, totalQty: total, totalPrice });
    }

    // 2) (Optional) Also expose globally & fire events for Navbar/Checkout compatibility
    if (typeof window !== "undefined") {
      const basket = { items, totalQty: total, totalPrice };
      window.basketItem = basket;

      // Persist for this tab only (survives route changes & reloads, cleared when tab closes)
      if (window.sessionStorage) {
        try {
          window.sessionStorage.setItem("basketItem", JSON.stringify(basket));
        } catch (_) {}
      }

      window.dispatchEvent(
        new CustomEvent("basket:update", { detail: basket })
      );

      // Back-compat with existing listeners
      window.__cartTotal = total;
      window.__cartItems = items;
      window.__cartTotalPrice = totalPrice;
      window.dispatchEvent(
        new CustomEvent("cart:update", { detail: { total } })
      );
      window.dispatchEvent(
        new CustomEvent("cart:items", { detail: { items, totalPrice } })
      );
    }
  }, [menuData, setBasketItem]);

  // Re-price the cart when menu prices change (e.g. an admin updates offers
  // while the page is open), instead of keeping the price from when "+" was tapped.
  const quantitiesRef = useRef(quantities);
  quantitiesRef.current = quantities;
  useEffect(() => {
    const q = quantitiesRef.current;
    if (Object.values(q).some((v) => (v || 0) > 0)) publishTotal(q);
  }, [publishTotal]);

  const inc = (id) =>
    setQuantities((q) => {
      if (isClosed) return q;
      const next = { ...q, [id]: Math.min((q[id] || 0) + 1, 99) };
      publishTotal(next);
      return next;
    });

  const dec = (id) =>
    setQuantities((q) => {
      if (isClosed) return q;
      const next = { ...q, [id]: Math.max((q[id] || 0) - 1, 0) };
      publishTotal(next);
      return next;
    });

  useEffect(() => {
    const incH = (e) => {
      const id = e.detail?.id;
      if (!id) return;
      setQuantities((q) => {
        const next = { ...q, [id]: Math.min((q[id] || 0) + 1, 99) };
        publishTotal(next);
        return next;
      });
    };
    const decH = (e) => {
      const id = e.detail?.id;
      if (!id) return;
      setQuantities((q) => {
        const next = { ...q, [id]: Math.max((q[id] || 0) - 1, 0) };
        publishTotal(next);
        return next;
      });
    };

    window.addEventListener("cart:increment", incH);
    window.addEventListener("cart:decrement", decH);

    return () => {
      window.removeEventListener("cart:increment", incH);
      window.removeEventListener("cart:decrement", decH);
    };
  }, [publishTotal]);

  useEffect(() => {
    // When menu data is ready, restore quantities from persisted basket (sessionStorage),
    // falling back to window/context. This does not require any backend.
    if (!menuData || menuData.length === 0) return;

    const hasQuantities = Object.values(quantities).some((v) => (v || 0) > 0);
    if (hasQuantities) return; // don't override user state

    let src = null;

    // 1) sessionStorage (tab-scoped persistence)
    if (typeof window !== "undefined" && window.sessionStorage) {
      try {
        const raw = window.sessionStorage.getItem("basketItem");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && Array.isArray(parsed.items) && parsed.items.length) {
            src = parsed;
          }
        }
      } catch (_) {}
    }

    // 2) window global
    if (
      !src &&
      typeof window !== "undefined" &&
      window.basketItem &&
      Array.isArray(window.basketItem.items) &&
      window.basketItem.items.length
    ) {
      src = window.basketItem;
    }

    // 3) Context fallback
    if (
      !src &&
      basketItem &&
      Array.isArray(basketItem.items) &&
      basketItem.items.length
    ) {
      src = basketItem;
    }

    if (src) {
      const map = {};
      src.items.forEach((it) => {
        if (it && it.id) map[it.id] = it.qty || 0;
      });
      // Set quantities from saved cart and broadcast once so Navbar/Checkout sync immediately
      setQuantities((q) => {
        if (Object.keys(q).length) return q; // keep if already set
        // publish using the restored map
        publishTotal(map);
        return map;
      });
    }
  }, [basketItem, menuData, publishTotal, quantities]);
  // console.log(quantities);

  return (
    <section id="menu" className="menu-section">
      <h2>{t("menu.title")}</h2>

      {isClosed && (
        <div
          style={{
            margin: "6px 0 12px",
            background: "#fff5f5",
            color: "#b2181e",
            border: "1px solid rgba(178,24,30,.18)",
            borderRadius: 10,
            padding: "10px 12px",
            fontWeight: 800,
            textAlign: "center",
          }}
        >
          {t(
            "checkout.closedBanner",
            "We\u2019re currently closed (4:00–12:00)."
          )}
        </div>
      )}

      <div className="custom-category-list">
        {[
          "Best Seller",
          "Offer",
          "All",
          "Chicken Shawarma",
          "Meat Shawarma",
          "Shawarma mix",
          "Chicken Comboo Meals",
          "Chicken Burgers",
          "Beef Burgers",
          "Fries",
          "Beverages",
          "Sauces",
        ].map((cat, i) => (
          <button
            key={i}
            className={`category-button ${
              selectedCategory === cat ? "active" : ""
            } ${cat === "Offer" ? "is-offer" : ""}`}
            onClick={() => {
              setSelectedCategory(cat);
              scrollToGrid();
            }}
          >
            <span className="icon">{categoryIcons[cat] || "📦"}</span>
            {t(`categories.${cat}`, cat)}
          </button>
        ))}
      </div>

      <h3
        ref={sectionRef}
        className="menu-section-heading"
      >
        {categoryIcons[selectedCategory] ? (
          <span className="icon" style={{ marginRight: 6 }}>
            {categoryIcons[selectedCategory]}
          </span>
        ) : null}
        {t(`categories.${selectedCategory}`, selectedCategory)}
      </h3>

      <div className="menu-grid" ref={gridRef}>
        {visibleItems.map((item) => {
          const lang = i18n.language || "en";
          const shortLang = lang.split("-")[0];
          const localizedName =
            item?.name?.[lang] ||
            item?.name?.[shortLang] ||
            item?.name?.en ||
            item?.name?.ar ||
            "";
          const localizedDescription =
            item?.description?.[lang] ||
            item?.description?.[shortLang] ||
            item?.description?.en ||
            item?.description?.ar ||
            "";

          return (
            <div key={item.id} className="menu-card">
              {item.isBestSeller && (
                <div className="ribbon">Best Seller</div>
              )}
              <LazyImage
                src={item.image}
                alt={localizedName}
                width={320}
                height={240}
                onClick={() =>
                  setPopupImage({
                    image: item.image,
                    description: localizedDescription,
                    name: localizedName,
                  })
                }
                style={{ cursor: "pointer" }}
              />
              <h3>{localizedName}</h3>
              {item.isBestSeller ? (
                <div className="price price--deal" aria-label="discounted price">
                  <span className="price-old">
                    {Number(item.price).toFixed(2)} AED
                  </span>
                  <span className="price-new">
                    {Number(item.discountPrice || item.price).toFixed(2)} AED
                  </span>
                </div>
              ) : (
                <span className="price">{Number(item.price).toFixed(2)} AED</span>
              )}
              <div className="item-actions">
                <div className="qty" role="group" aria-label="Quantity selector">
                  <button
                    type="button"
                    className="qty__btn"
                    aria-label="Decrease quantity"
                    onClick={() => dec(item.id)}
                    disabled={isClosed}
                    aria-disabled={isClosed}
                    title={isClosed ? "Closed 4:00–12:00" : undefined}
                    style={
                      isClosed
                        ? { opacity: 0.5, cursor: "not-allowed" }
                        : undefined
                    }
                  >
                    −
                  </button>
                  <div className="qty__value" aria-live="polite">
                    {quantities[item.id] || 0}
                  </div>
                  <button
                    type="button"
                    className="qty__btn"
                    aria-label="Increase quantity"
                    onClick={() => inc(item.id)}
                    disabled={isClosed}
                    aria-disabled={isClosed}
                    title={isClosed ? "Closed 4:00–12:00" : undefined}
                    style={
                      isClosed
                        ? { opacity: 0.5, cursor: "not-allowed" }
                        : undefined
                    }
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {hasHiddenItems && (
        <div className="menu-load-more">
          <button type="button" onClick={() => setShowAll(true)}>
            {t("menu.loadMore", "Load more")}
          </button>
        </div>
      )}

      {popupImage && (
        <div className="image-popup">
          <button className="close-button" onClick={() => setPopupImage(null)}>
            ×
          </button>
          <div>
            <LazyImage
              src={popupImage.image}
              alt={popupImage.name || "menu item"}
              width={500}
              height={375}
              loading="eager"
              decoding="auto"
            />
            <h3>{popupImage.name}</h3>
            <div
              style={{ color: "#fff", marginTop: "1rem", textAlign: "center" }}
            >
              {popupImage.description
                ? documentToReactComponents(popupImage.description, options)
                : null}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Menu;
